import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { NoPaymentNote } from '../components/NoPaymentNote';
import { Shell } from '../components/Shell';
import { submitOrder } from '../lib/api';
import { cart, useCart } from '../lib/cart';
import { useSetting } from '../lib/settings';

export const Route = createFileRoute('/checkout')({
  component: CheckoutPage,
});

const FormSchema = z.object({
  name: z.string().min(1),
  phone: z
    .string()
    .min(6)
    .max(20)
    .regex(/^[+0-9\s-]+$/, 'phone_invalid'),
  city: z.string().min(1).max(80),
  note: z.string().max(500).optional(),
});
type FormValues = z.infer<typeof FormSchema>;

function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = useCart();

  useEffect(() => {
    if (items.length === 0) void navigate({ to: '/cart' });
  }, [items.length, navigate]);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: '', phone: '', city: '', note: '' },
  });

  const waNumber = useSetting('contact.whatsappNumber');
  const waTemplate = useSetting('contact.whatsappMessage');

  const mutation = useMutation({
    mutationFn: submitOrder,
    onSuccess: ({ id }) => {
      cart.clear();
      // Build WhatsApp URL and open it — this is in user gesture chain (form submit) so it won't be blocked
      const orderLink = `${window.location.origin}/order/${id}`;
      let text = waTemplate.replace('{{orderId}}', id).replace('{{orderLink}}', orderLink);
      // If template didn't have {{orderLink}}, append the link
      if (!waTemplate.includes('{{orderLink}}')) text += `\n${orderLink}`;
      const msg = encodeURIComponent(text);
      window.open(`https://wa.me/${waNumber}?text=${msg}`, '_blank');
      // Then navigate to the order detail page
      void navigate({ to: '/order/$id', params: { id } });
    },
  });

  if (items.length === 0) return null;

  return (
    <Shell showBack backTo="/cart">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="pt-6 space-y-5 pb-16"
      >
        <div>
          <h1 className="text-2xl font-black text-oud-900">{t('initiate.title')}</h1>
          <p className="text-sm text-oud-600 mt-1">{t('initiate.subtitle')}</p>
        </div>

        <form
          className="space-y-4"
          onSubmit={form.handleSubmit((values) =>
            mutation.mutate({
              items: items.map((i) => ({
                quote_id: i.quote_id,
                qty: i.qty,
                notes: i.notes,
              })),
              name: values.name,
              phone: values.phone,
              city: values.city,
              note: values.note || undefined,
            }),
          )}
        >
          <Field label={t('lead.name')} error={form.formState.errors.name?.message}>
            <input
              {...form.register('name')}
              placeholder={t('lead.name_placeholder')}
              className="input"
              autoComplete="name"
            />
          </Field>

          <Field label={t('lead.phone')} error={form.formState.errors.phone?.message}>
            <input
              {...form.register('phone')}
              placeholder={t('lead.phone_placeholder')}
              className="input"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
            />
          </Field>

          <Field label={t('lead.city')} error={form.formState.errors.city?.message}>
            <input
              {...form.register('city')}
              placeholder={t('lead.city_placeholder')}
              className="input"
            />
          </Field>

          <Field label={t('lead.note')} error={form.formState.errors.note?.message}>
            <textarea
              {...form.register('note')}
              rows={3}
              placeholder={t('lead.note_placeholder')}
              className="input resize-none"
            />
          </Field>

          {mutation.error ? (
            <p className="text-sm text-red-800 bg-red-50 rounded-xl px-3 py-2">
              {mutation.error instanceof Error ? mutation.error.message : ''}
            </p>
          ) : null}

          <NoPaymentNote />

          <Button size="lg" fullWidth type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('initiate.submitting') : t('initiate.submit')}
          </Button>
        </form>

        <style>{`
          .input {
            width: 100%;
            background: #f5ebdd;
            border: 1px solid rgba(232, 208, 173, 0.6);
            border-radius: 1rem;
            padding: 0.875rem 1rem;
            color: #2e1d10;
            font-size: 1rem;
            transition: border-color 0.15s, background 0.15s;
          }
          .input::placeholder { color: rgba(46, 29, 16, 0.4); }
          .input:focus { outline: none; border-color: #c9904e; background: #fbf7f2; }
        `}</style>
      </motion.div>
    </Shell>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-bold text-oud-800">{label}</span>
      {children}
      {error ? <span className="block text-xs text-red-800">{error}</span> : null}
    </label>
  );
}
